import React, { useEffect, useState, useCallback } from "react";
import { Rnd } from "react-rnd";
import { getDocumentPages, addField } from "../../api/adminApi";
import { useToast } from "../common/ToastProvider";

const DISPLAY_SCALE = 0.5;

export default function PdfPagePlacement({
  documentId,
  signerId,
  readOnly = false,
}) {
  const [pages, setPages] = useState([]);
  const [fields, setFields] = useState([]);

  const toast = useToast();

  const loadPages = useCallback(async () => {
    try {
      const res = await getDocumentPages(documentId);
      const mappedPages = res.data.map((page) => {
        const displayWidth = Math.round(page.width * DISPLAY_SCALE);
        const displayHeight = Math.round(page.height * DISPLAY_SCALE);
        return {
          ...page,
          displayWidth,
          displayHeight,
        };
      });
      setPages(mappedPages);
    } catch (e) {
      toast.error("Failed to load document pages");
    }
  }, [documentId, toast]);

  useEffect(() => {
    if (documentId) {
      loadPages();
    }
  }, [documentId, loadPages]);

  const addNewField = (pageNumber) => {
    if (readOnly) return;

    const page = pages.find((p) => p.pageNumber === pageNumber);
    if (!page) return;

    const defaultWidth = 180;
    const defaultHeight = 60;

    const newField = {
      id: Date.now(),
      pageNumber,
      x: Math.max(0, Math.min(50, page.displayWidth - defaultWidth)),
      y: Math.max(0, Math.min(50, page.displayHeight - defaultHeight)),
      width: defaultWidth,
      height: defaultHeight,
      saved: false,
    };

    setFields((prev) => [...prev, newField]);
  };

  const updateField = (id, updates) => {
    if (readOnly) return;

    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    );
  };

  const removeField = (id) => {
    if (readOnly) return;
    setFields((prev) => prev.filter((f) => f.id !== id));
  };

  const clampFieldToPage = (field, page) => {
    const safeWidth = Math.min(field.width, page.displayWidth);
    const safeHeight = Math.min(field.height, page.displayHeight);

    const safeX = Math.max(0, Math.min(field.x, page.displayWidth - safeWidth));
    const safeY = Math.max(
      0,
      Math.min(field.y, page.displayHeight - safeHeight),
    );

    return {
      ...field,
      x: safeX,
      y: safeY,
      width: safeWidth,
      height: safeHeight,
    };
  };

  const saveField = async (field, page) => {
    if (readOnly) return;

    if (!signerId) {
      toast.error("Select signer first");
      return;
    }

    try {
      const clampedField = clampFieldToPage(field, page);

      const xRatio = clampedField.x / page.displayWidth;
      const yRatio = clampedField.y / page.displayHeight;
      const widthRatio = clampedField.width / page.displayWidth;
      const heightRatio = clampedField.height / page.displayHeight;

      await addField(documentId, {
        signerId,
        fieldType: "SIGNATURE",
        pageNumber: clampedField.pageNumber,
        xRatio,
        yRatio,
        widthRatio,
        heightRatio,
        requiredFlag: true,
      });

      toast.success("Field added");

      // Keep the field visible after saving so the user sees persisted placements
      setFields((prev) =>
        prev.map((f) => (f.id === field.id ? { ...f, saved: true } : f)),
      );
    } catch (e) {
      toast.error("Failed to save field");
    }
  };

  return (
    <div className="card">
      <h3 className="card-title">PDF Field Placement</h3>
      <p className="card-subtitle">
        Place signature boxes visually on document pages. Saved positions will
        be converted to ratio-based coordinates for accurate placement.
      </p>

      {readOnly ? (
        <div className="info-banner">
          This document is locked. Field placement is view-only.
        </div>
      ) : (
        <div className="warn-banner">
          Drag and resize a signature box to the correct location, then save it
          for the selected signer.
        </div>
      )}

      {pages.length === 0 ? (
        <div className="empty-state">No pages available for placement.</div>
      ) : (
        pages.map((page) => (
          <div key={page.pageNumber} className="placement-page">
            <div className="placement-toolbar">
              <div>
                <strong>Page {page.pageNumber}</strong>
              </div>

              {!readOnly && (
                <button
                  className="btn btn-primary"
                  onClick={() => addNewField(page.pageNumber)}
                >
                  Add Signature Field
                </button>
              )}
            </div>

            <div
              className="placement-canvas"
              style={{
                width: `${page.displayWidth}px`,
                height: `${page.displayHeight}px`,
              }}
            >
              <img
                src={page.imageUrl}
                alt={`Page ${page.pageNumber}`}
                style={{
                  width: `${page.displayWidth}px`,
                  height: `${page.displayHeight}px`,
                  display: "block",
                }}
              />

              {fields
                .filter((f) => f.pageNumber === page.pageNumber)
                .map((field) => (
                  <Rnd
                    key={field.id}
                    size={{ width: field.width, height: field.height }}
                    position={{ x: field.x, y: field.y }}
                    bounds="parent"
                    disableDragging={readOnly}
                    enableResizing={!readOnly}
                    onDragStop={(e, d) => {
                      const safeX = Math.max(
                        0,
                        Math.min(d.x, page.displayWidth - field.width),
                      );
                      const safeY = Math.max(
                        0,
                        Math.min(d.y, page.displayHeight - field.height),
                      );

                      updateField(field.id, {
                        x: safeX,
                        y: safeY,
                      });
                    }}
                    onResizeStop={(e, dir, ref, delta, pos) => {
                      const newWidth = Math.min(
                        ref.offsetWidth,
                        page.displayWidth,
                      );
                      const newHeight = Math.min(
                        ref.offsetHeight,
                        page.displayHeight,
                      );

                      const safeX = Math.max(
                        0,
                        Math.min(pos.x, page.displayWidth - newWidth),
                      );
                      const safeY = Math.max(
                        0,
                        Math.min(pos.y, page.displayHeight - newHeight),
                      );

                      updateField(field.id, {
                        x: safeX,
                        y: safeY,
                        width: newWidth,
                        height: newHeight,
                      });
                    }}
                    style={{
                      border: "2px dashed #ef4444",
                      background: "rgba(239, 68, 68, 0.10)",
                      borderRadius: 10,
                      zIndex: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        padding: 8,
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          marginBottom: 8,
                        }}
                      >
                        SIGN FIELD
                      </div>

                      {!readOnly && !field.saved && (
                        <div
                          className="inline-actions"
                          style={{ justifyContent: "center" }}
                        >
                          <button
                            className="btn btn-primary"
                            onClick={() => saveField(field, page)}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn-light"
                            onClick={() => removeField(field.id)}
                          >
                            Remove
                          </button>
                        </div>
                      )}

                      {field.saved && (
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#10b981",
                          }}
                        >
                          Saved
                        </div>
                      )}
                    </div>
                  </Rnd>
                ))}
            </div>

            <div className="helper-text" style={{ marginTop: 8 }}>
              Original page size: {page.width} × {page.height}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
